import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    Snackbar,
    Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllSets, updateSet, deleteSet, isTrackerSetActive, getGenericTrackerSetName } from '../../services/tracker-limited-data';

const SetManager = ({ onBack }) => {
    const [sets, setSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        console.log('SetManager mounted, fetching sets...');
        fetchSets();
    }, []);

    const fetchSets = async () => {
        try {
            setLoading(true);
            console.log('Fetching sets...');
            const setsData = await getAllSets();
            console.log('Raw sets data:', setsData);
            // Ustaw domyślnie is_active na true dla setów EXPANSION, jeśli nie jest ustawione
            const setsWithDefaults = setsData.map(set => ({
                ...set,
                id: String(set.id), // Konwertuj ID na string
                is_active: set.type === 'EXPANSION' ? (set.is_active !== false) : false,
                tracker_active: isTrackerSetActive(set)
            }));

            // Sprawdź, czy wszystkie sety mają poprawne ID
            const validSets = setsWithDefaults.filter(set => {
                if (!set.id || set.id === 'undefined' || set.id === 'null') {
                    console.error('Set without valid ID:', set);
                    return false;
                }
                return true;
            });

            console.log('Sets with defaults:', setsWithDefaults);
            console.log('Valid sets (with ID):', validSets);

            if (validSets.length === 0) {
                setError('Wszystkie sety mają niepoprawne ID. Sprawdź dane w Firestore.');
                return;
            }

            setSets(validSets);
            setError(null);
        } catch (err) {
            console.error('Błąd podczas pobierania setów:', err);
            setError('Nie udało się pobrać setów. Sprawdź połączenie z internetem.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleSet = (setId) => {
        console.log('Toggle set:', setId, 'typ:', typeof setId);
        // Konwertuj setId na string dla porównania
        const setIdStr = String(setId);
        setSets(prevSets =>
            prevSets.map(set => {
                const setIdStr2 = String(set.id);
                return setIdStr2 === setIdStr
                    ? { ...set, is_active: !set.is_active }
                    : set;
            })
        );
    };

    const handleToggleTrackerSet = (setId) => {
        const setIdStr = String(setId);
        setSets(prevSets =>
            prevSets.map(set => {
                const setIdStr2 = String(set.id);
                return setIdStr2 === setIdStr
                    ? { ...set, tracker_active: !set.tracker_active }
                    : set;
            })
        );
    };

    const handleSaveChanges = async () => {
        try {
            setSaving(true);

            // Znajdź sety typu EXPANSION (tylko te można zmieniać)
            const expansionSets = sets.filter(set => set.type === 'EXPANSION');
            console.log('Expansion sets to save:', expansionSets);

            if (expansionSets.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'Brak setów typu EXPANSION do zapisania',
                    severity: 'warning'
                });
                return;
            }

            // Sprawdź, czy wszystkie sety EXPANSION mają poprawne ID
            const validExpansionSets = expansionSets.filter(set => {
                if (!set.id || set.id === 'undefined' || set.id === 'null') {
                    console.error('Expansion set without valid ID:', set);
                    return false;
                }
                return true;
            });

            if (validExpansionSets.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'Wszystkie sety EXPANSION mają niepoprawne ID',
                    severity: 'error'
                });
                return;
            }

            console.log('Valid expansion sets to save:', validExpansionSets);

            // Zapisz zmiany w Firestore
            for (const set of validExpansionSets) {
                const setIdStr = String(set.id);
                console.log('Saving set:', setIdStr, 'is_active:', set.is_active, 'tracker_active:', set.tracker_active);
                await updateSet(setIdStr, {
                    is_active: set.is_active,
                    tracker_active: set.tracker_active
                });
            }

            setSnackbar({
                open: true,
                message: `Pomyślnie zapisano zmiany dla ${validExpansionSets.length} setów`,
                severity: 'success'
            });

            // Odśwież dane
            await fetchSets();
        } catch (err) {
            console.error('Błąd podczas zapisywania zmian:', err);
            setSnackbar({
                open: true,
                message: 'Błąd podczas zapisywania zmian',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteIntegrationSet = async () => {
        try {
            setSaving(true);
            const integrationSet = sets.find((set) => {
                const id = String(set.id || '').toLowerCase();
                const name = String(set.name || '').toLowerCase();
                return id.includes('integration') || name.includes('integration set');
            });
            if (!integrationSet) {
                setSnackbar({
                    open: true,
                    message: 'Nie znaleziono Integration set',
                    severity: 'info'
                });
                return;
            }
            await deleteSet(integrationSet.id);
            await fetchSets();
            setSnackbar({
                open: true,
                message: 'Integration set został usunięty',
                severity: 'success'
            });
        } catch (err) {
            console.error('Błąd podczas usuwania Integration set:', err);
            setSnackbar({
                open: true,
                message: 'Nie udało się usunąć Integration set',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'EXPANSION':
                return 'success';
            case 'ENCHANTED':
                return 'warning';
            case 'SPECIAL':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusColor = (isActive) => {
        return (isActive !== false) ? 'success' : 'default';
    };

    const getSetDisplayName = (set) => {
        if (set.type !== 'EXPANSION') return set.name || set.id;
        return getGenericTrackerSetName(set.id) || set.name || set.id;
    };

    const getSetSecondaryName = (set) => {
        const displayName = getSetDisplayName(set);
        const originalName = set.name || set.id;
        if (originalName === displayName) return set.id;
        return `${originalName} · ${set.id}`;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button onClick={fetchSets} variant="contained">
                    Spróbuj ponownie
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Nagłówek */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => {
                    console.log('Back button clicked');
                    onBack();
                }} sx={{ color: 'white' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
                    Zarządzanie Setami
                </Typography>
            </Box>

            <Typography variant="body1" sx={{ color: 'rgb(156, 163, 175)', mb: 3 }}>
                Ustaw aktywność setów osobno dla Limited Tierlist (`is_active`) i osobno dla Trackera (`tracker_active`).
            </Typography>

            {/* Tabela setów */}
            <Paper sx={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(76, 29, 149, 0.3)', mb: 3 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(76, 29, 149, 0.15)' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem' }}>Nazwa setu</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', textAlign: 'center' }}>Typ</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', textAlign: 'center' }}>Numer sortowania</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', textAlign: 'center' }}>Status</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', textAlign: 'center' }}>Limited aktywny</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', textAlign: 'center' }}>Tracker aktywny</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sets.map((set, index) => (
                                <TableRow
                                    key={set.id}
                                    sx={{
                                        '&:hover': { backgroundColor: 'rgba(76, 29, 149, 0.08)' },
                                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(76, 29, 149, 0.02)',
                                        transition: 'background-color 0.2s ease-in-out'
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                            {getSetDisplayName(set)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.65)' }}>
                                            {getSetSecondaryName(set)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip
                                            label={set.type}
                                            size="small"
                                            color={getTypeColor(set.type)}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                            {set.sort_number || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip
                                            label={(set.is_active !== false) ? 'Aktywny' : 'Nieaktywny'}
                                            size="small"
                                            color={getStatusColor(set.is_active)}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Switch
                                            checked={set.is_active !== false}
                                            onChange={() => handleToggleSet(set.id)}
                                            disabled={set.type !== 'EXPANSION'}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: 'rgb(34, 197, 94)',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: 'rgb(34, 197, 94)',
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Switch
                                            checked={set.tracker_active !== false}
                                            onChange={() => handleToggleTrackerSet(set.id)}
                                            disabled={set.type !== 'EXPANSION'}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: 'rgb(59, 130, 246)',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: 'rgb(59, 130, 246)',
                                                },
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Przycisk zapisz */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleSaveChanges}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    sx={{
                        backgroundColor: 'rgb(76, 29, 149)',
                        '&:hover': { backgroundColor: 'rgb(91, 33, 182)' },
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem'
                    }}
                >
                    {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteIntegrationSet}
                    disabled={saving}
                >
                    Usuń Integration set
                </Button>
            </Box>

            {/* Informacja */}
            <Paper sx={{
                backgroundColor: 'rgba(76, 29, 149, 0.1)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                p: 2,
                borderRadius: '8px'
            }}>
                <Typography variant="body2" sx={{ color: 'rgb(156, 163, 175)', textAlign: 'center' }}>
                    <strong>Uwaga:</strong> Tylko sety typu EXPANSION mogą być aktywowane.
                    Sety ENCHANTED i SPECIAL są automatycznie nieaktywne i nie mogą być zmienione.
                </Typography>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SetManager; 
